import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  CircularProgress,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { usePageTitle } from '../hooks/usePageTitle';
import { apiClient } from '../services/api/client';

interface KnowledgeEntry {
  id: number;
  category: string;
  topic: string;
  content: string;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

interface HistoryEntry {
  version: number;
  topic: string;
  content: string;
  category: string;
  priority: number;
  changed_by: string;
  changed_at: string;
  change_type: string;
}

const categories = [
  { value: '', label: 'All Categories' },
  { value: 'market_terminology', label: 'Market Terminology' },
  { value: 'data_interpretation', label: 'Data Interpretation' },
  { value: 'tool_usage', label: 'Tool Usage' },
  { value: 'corrections', label: 'Corrections' },
  { value: 'general_advice', label: 'General Advice' },
  { value: 'best_practices', label: 'Best Practices' },
  { value: 'error_handling', label: 'Error Handling' },
];

export const MiaKnowledgeBase: React.FC = () => {
  usePageTitle('Mia Knowledge Base');
  const { enqueueSnackbar } = useSnackbar();

  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<KnowledgeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntry | null>(null);
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);

  // Form states
  const [formCategory, setFormCategory] = useState('corrections');
  const [formTopic, setFormTopic] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formPriority, setFormPriority] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadEntries();
  }, []);

  useEffect(() => {
    filterEntries();
  }, [entries, searchTerm, categoryFilter]);

  const loadEntries = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: {
          entries: KnowledgeEntry[];
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      }>('/api/mobile/admin/knowledge?isActive=true');
      setEntries(response.data.entries);
    } catch (error: any) {
      console.error('Error loading knowledge entries:', error);
      enqueueSnackbar('Failed to load knowledge entries', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const filterEntries = () => {
    let filtered = [...entries];

    if (categoryFilter) {
      filtered = filtered.filter((e) => e.category === categoryFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.topic.toLowerCase().includes(term) ||
          e.content.toLowerCase().includes(term)
      );
    }

    setFilteredEntries(filtered);
  };

  const handleCreateNew = () => {
    setSelectedEntry(null);
    setFormCategory('corrections');
    setFormTopic('');
    setFormContent('');
    setFormPriority(5);
    setEditModalOpen(true);
  };

  const handleEdit = (entry: KnowledgeEntry) => {
    setSelectedEntry(entry);
    setFormCategory(entry.category);
    setFormTopic(entry.topic);
    setFormContent(entry.content);
    setFormPriority(entry.priority);
    setEditModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formTopic.trim() || !formContent.trim()) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
      return;
    }

    setIsSubmitting(true);
    try {
      if (selectedEntry) {
        // Update existing entry
        await apiClient.put(`/api/mobile/admin/knowledge/${selectedEntry.id}`, {
          category: formCategory,
          topic: formTopic.trim(),
          content: formContent.trim(),
          priority: formPriority,
        });
        enqueueSnackbar('Knowledge entry updated successfully', { variant: 'success' });
      } else {
        // Create new entry
        await apiClient.post('/api/mobile/admin/knowledge', {
          category: formCategory,
          topic: formTopic.trim(),
          content: formContent.trim(),
          priority: formPriority,
        });
        enqueueSnackbar('Knowledge entry created successfully', { variant: 'success' });
      }

      setEditModalOpen(false);
      loadEntries();
    } catch (error: any) {
      console.error('Error saving knowledge entry:', error);
      enqueueSnackbar(error.message || 'Failed to save knowledge entry', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this knowledge entry?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/mobile/admin/knowledge/${id}`);
      enqueueSnackbar('Knowledge entry deleted successfully', { variant: 'success' });
      loadEntries();
    } catch (error: any) {
      console.error('Error deleting knowledge entry:', error);
      enqueueSnackbar('Failed to delete knowledge entry', { variant: 'error' });
    }
  };

  const handleViewHistory = async (entry: KnowledgeEntry) => {
    setSelectedEntry(entry);
    setHistoryModalOpen(true);

    try {
      const response = await apiClient.get<{ success: boolean; data: HistoryEntry[] }>(
        `/api/mobile/admin/knowledge/${entry.id}/history`
      );
      setHistoryEntries(response.data);
    } catch (error: any) {
      console.error('Error loading history:', error);
      enqueueSnackbar('Failed to load version history', { variant: 'error' });
      setHistoryEntries([]);
    }
  };

  const getCategoryLabel = (value: string) => {
    return categories.find((c) => c.value === value)?.label || value;
  };

  return (
    <Box sx={{ pb: 10, px: 2, pt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Mia's Memory
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          sx={{
            backgroundColor: '#F5A800',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#D89500',
            },
          }}
        >
          Add Knowledge
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage Mia's permanent knowledge base. Entries here are used in all conversations.
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search topic or content..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ flex: 1 }}
        />
        <TextField
          select
          size="small"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {categories.map((cat) => (
            <MenuItem key={cat.value} value={cat.value}>
              {cat.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Loading State */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#F5A800' }} />
        </Box>
      ) : filteredEntries.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            opacity: 0.5,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No knowledge entries found
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {searchTerm || categoryFilter
              ? 'Try adjusting your filters'
              : 'Click "Add Knowledge" to create your first entry'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredEntries.map((entry) => (
            <Card key={entry.id} elevation={1}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1rem' }}>
                    {entry.topic}
                  </Typography>
                  <Chip
                    label={getCategoryLabel(entry.category)}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(245, 168, 0, 0.1)',
                      color: '#F5A800',
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    whiteSpace: 'pre-wrap',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {entry.content}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label={`Priority: ${entry.priority}`} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    Updated {new Date(entry.updated_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2, pt: 0 }}>
                <IconButton size="small" onClick={() => handleViewHistory(entry)} title="View History">
                  <HistoryIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleEdit(entry)} title="Edit">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(entry.id)} title="Delete">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Edit/Create Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {selectedEntry ? 'Edit Knowledge Entry' : 'Create Knowledge Entry'}
          </Typography>
          <IconButton size="small" onClick={() => setEditModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Category"
            value={formCategory}
            onChange={(e) => setFormCategory(e.target.value)}
            margin="normal"
            required
          >
            {categories.filter((c) => c.value !== '').map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Topic"
            placeholder="Brief title for this knowledge entry"
            value={formTopic}
            onChange={(e) => setFormTopic(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Content"
            placeholder="Knowledge content for Mia"
            value={formContent}
            onChange={(e) => setFormContent(e.target.value)}
            margin="normal"
            multiline
            rows={6}
            required
          />
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography variant="body2" gutterBottom>
              Priority
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Higher priority knowledge is emphasized more (1-10)
            </Typography>
            <Slider
              value={formPriority}
              onChange={(_, value) => setFormPriority(value as number)}
              min={1}
              max={10}
              step={1}
              marks
              valueLabelDisplay="on"
              sx={{ color: '#F5A800' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditModalOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting || !formTopic.trim() || !formContent.trim()}
            sx={{
              backgroundColor: '#F5A800',
              '&:hover': { backgroundColor: '#D89500' },
            }}
          >
            {isSubmitting ? 'Saving...' : selectedEntry ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Modal */}
      <Dialog
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            Version History: {selectedEntry?.topic}
          </Typography>
          <IconButton size="small" onClick={() => setHistoryModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {historyEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              No version history available
            </Typography>
          ) : (
            <List disablePadding>
              {historyEntries.map((history, index) => (
                <React.Fragment key={history.version}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                      <Typography variant="body2" fontWeight="600">
                        Version {history.version} - {history.change_type}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(history.changed_at).toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Changed by: {history.changed_by}
                    </Typography>
                    <Chip label={getCategoryLabel(history.category)} size="small" sx={{ mb: 1 }} />
                    <Typography variant="body2" fontWeight="600" mb={0.5}>
                      {history.topic}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {history.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" mt={1}>
                      Priority: {history.priority}
                    </Typography>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHistoryModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
